import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import DashboardHeader from '../components/DashboardHeader';
import { useAuth } from '../context/AuthContext';
import { FaBriefcase, FaFileAlt, FaUserEdit, FaSignOutAlt, FaHospital, FaMapMarkerAlt, FaRupeeSign, FaCalendarAlt, FaClock, FaMoneyBillAlt, FaList, FaTh, FaTimes } from 'react-icons/fa';
import '../App.css';
import client from '../api/client';
import { toast } from 'react-toastify';


function ProfessionalDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('opportunities');
  const [districtFilter, setDistrictFilter] = useState(null); // Will be set from API (idDistrict)
  const [districtFilterName, setDistrictFilterName] = useState('All'); // For display
  const [professionFilter, setProfessionFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All'); // Status filter for Work History
  const [opportunityStatusFilter, setOpportunityStatusFilter] = useState('All'); // Status filter for Open Opportunities
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  // API data states
  const [districts, setDistricts] = useState([]);
  const [currentProfessional, setCurrentProfessional] = useState(null);
  const [appliedRequests, setAppliedRequests] = useState([]);
  const [openOpportunities, setOpenOpportunities] = useState([]);

  // Loading states
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [isLoadingProfessional, setIsLoadingProfessional] = useState(false);
  const [isLoadingJobListings, setIsLoadingJobListings] = useState(false);
  const [isLoadingWorkHistory, setIsLoadingWorkHistory] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [applyingJobId, setApplyingJobId] = useState(null);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawingJobId, setWithdrawingJobId] = useState(null);

  // Track if district filter has been initialized from professional data
  const [isDistrictInitialized, setIsDistrictInitialized] = useState(false);

  // Work Status Update Modal state
  const [showWorkStatusModal, setShowWorkStatusModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [periodType, setPeriodType] = useState('for a shift');
  const [workStatus, setWorkStatus] = useState('');
  const [actualStartTime, setActualStartTime] = useState('');
  const [actualCompletionTime, setActualCompletionTime] = useState('');
  const [actualStartDate, setActualStartDate] = useState('');
  const [actualCompletionDate, setActualCompletionDate] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [remarks, setRemarks] = useState('');

  // Format date for display (DD.MMM.YYYY)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };
  const formatINRNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(String(value).replace(/,/g, ''));
    if (Number.isNaN(num)) return '';
  
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };
  // Fetch districts
  const fetchDistricts = async () => {
    setIsLoadingDistricts(true);
    try {
      const response = await client.get('/api/districts');
      const list = response.data?.data || [];
      setDistricts(list);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load districts.');
      setDistricts([]);
    } finally {
      setIsLoadingDistricts(false);
    }
  };
  const fetchJobPeriodAndRate = async (jobId) => {
    try {
      const response = await client.get(`/api/healthcare-professionals/jobs/${jobId}/period-rate`);
      if (response.data?.success) {
        return response.data.data;
      }
      toast.error(response.data?.message || 'Unable to fetch job details');
      return null;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to fetch job details');
      return null;
    }
  };

  // Fetch current professional data
  const fetchCurrentProfessional = async () => {
    setIsLoadingProfessional(true);
    try {
      const response = await client.get('/api/healthcare-professionals/current');
      const data = response.data?.data;
      if (data) {
        setCurrentProfessional(data);
        // Set default district filter from professional's idDistrict
        if (data.idDistrict && data.idDistrict > 0) {
          setDistrictFilter(data.idDistrict);
          // Find district name
          const district = districts.find(d => d.idDistrict === data.idDistrict);
          if (district) {
            setDistrictFilterName(district.districtName);
          }
        }
        // Mark district as initialized (even if idDistrict is 0 or null, we've checked)
        setIsDistrictInitialized(true);
      } else {
        // No data, but mark as initialized so we can proceed
        setIsDistrictInitialized(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load professional data.');
      // Even on error, mark as initialized so user can still use the app
      setIsDistrictInitialized(true);
    } finally {
      setIsLoadingProfessional(false);
    }
  };

  // Fetch job listings (Open Opportunities)
  const fetchJobListings = async () => {
    setIsLoadingJobListings(true);
    try {
      const params = {};
      if (districtFilter) {
        params.idDistrict = districtFilter;
      }
      const response = await client.get('/api/healthcare-professionals/job-listings', { params });
      const list = response.data?.data || [];
      setOpenOpportunities(list);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load job listings.');
      setOpenOpportunities([]);
    } finally {
      setIsLoadingJobListings(false);
    }
  };

  // Fetch work history (Applied Requests)
  const fetchWorkHistory = async () => {
    setIsLoadingWorkHistory(true);
    try {
      const params = {};
      if (districtFilter) {
        params.idDistrict = districtFilter;
      }
      const response = await client.get('/api/healthcare-professionals/work-history', { params });
      const list = response.data?.data || [];
      setAppliedRequests(list);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load work history.');
      setAppliedRequests([]);
    } finally {
      setIsLoadingWorkHistory(false);
    }
  };

  // Fetch districts on mount
  useEffect(() => {
    fetchDistricts();
  }, []);

  // Fetch current professional after districts are loaded
  useEffect(() => {
    if (districts.length > 0) {
      fetchCurrentProfessional();
    }
  }, [districts]);

  // Fetch job listings when district filter changes or when switching to opportunities tab
  // Only fetch after district has been initialized to avoid loading all data first
  useEffect(() => {
    if (activeTab === 'opportunities' && isDistrictInitialized) {
      fetchJobListings();
    }
  }, [districtFilter, activeTab, isDistrictInitialized]);

  // Fetch work history when district filter changes or when switching to applied tab
  // Only fetch after district has been initialized to avoid loading all data first
  useEffect(() => {
    if (activeTab === 'applied' && isDistrictInitialized) {
      fetchWorkHistory();
    }
  }, [districtFilter, activeTab, isDistrictInitialized]);

  // Handle district filter change
  const handleDistrictFilterChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === 'All' || selectedValue === '') {
      setDistrictFilter(null);
      setDistrictFilterName('All');
    } else {
      const districtId = Number(selectedValue);
      setDistrictFilter(districtId);
      const district = districts.find(d => d.idDistrict === districtId);
      if (district) {
        setDistrictFilterName(district.districtName);
      }
    }
    // Ensure district is marked as initialized when user manually changes it
    if (!isDistrictInitialized) {
      setIsDistrictInitialized(true);
    }
  };

  // Handle Modify Profile
  const handleModifyProfile = () => {
    navigate('/professional-profile');
  };

  // Handle Apply to Opportunity
  const handleApply = async (opportunityId) => {
    if (!window.confirm('Are you sure you want to apply for this opportunity?')) {
      return;
    }

    setIsApplying(true);
    setApplyingJobId(opportunityId);
    try {
      const response = await client.post('/api/healthcare-professionals/job-applications', {
        jobID: opportunityId
      });

      if (response.data?.success) {
        toast.success(response.data?.message || 'Application submitted successfully!');
        // Refresh job listings to show updated data
        await fetchJobListings();
      } else {
        toast.error(response.data?.message || 'Failed to submit application.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsApplying(false);
      setApplyingJobId(null);
    }
  };

  // Handle Withdraw Job
  const handleWithdraw = async (jobId, applicationId) => {
    if (!window.confirm('Are you sure you want to withdraw?')) {
      return;
    }

    setIsWithdrawing(true);
    setWithdrawingJobId(jobId);
    try {
      const response = await client.post('/api/healthcare-professionals/job/withdraw', {
        jobID: jobId,
        applicationID: applicationId
      });

      if (response.data?.success) {
        toast.success(response.data?.message || 'Job withdrawn successfully!');
        // Refresh work history to show updated data
        await fetchWorkHistory();
      } else {
        toast.error(response.data?.message || 'Failed to withdraw job.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to withdraw job. Please try again.');
    } finally {
      setIsWithdrawing(false);
      setWithdrawingJobId(null);
    }
  };

  // Handle Work Status Update
  const handleWorkStatusUpdate = async (jobId) => {
    setSelectedRequestId(jobId);

    const jobData = await fetchJobPeriodAndRate(jobId);
    if (!jobData) return;

    setSelectedRequest(jobData);

    // Bind API response
    setPeriodType(
      jobData.jobDurationType?.toLowerCase() === 'for a shift'
        ? 'for a shift'
        : 'for a period'
    );

    setWorkStatus('');
    
    // Bind actual start and completion dates/times from API
    if (jobData.jobDurationType?.toLowerCase() === 'for a shift') {
      // For shift: convert DateTime to datetime-local format (YYYY-MM-DDTHH:MM)
      if (jobData.actualWorkStartDateTime) {
        const startDate = new Date(jobData.actualWorkStartDateTime);
        const startDateString = startDate.toISOString().slice(0, 16);
        setActualStartTime(startDateString);
      } else {
        setActualStartTime('');
      }
      
      if (jobData.actualWorkCompleteDateTime) {
        const endDate = new Date(jobData.actualWorkCompleteDateTime);
        const endDateString = endDate.toISOString().slice(0, 16);
        setActualCompletionTime(endDateString);
      } else {
        setActualCompletionTime('');
      }
      
      setActualStartDate('');
      setActualCompletionDate('');
    } else {
      // For period: convert DateTime to date format (YYYY-MM-DD)
      if (jobData.actualWorkStartDateTime) {
        const startDate = new Date(jobData.actualWorkStartDateTime);
        const startDateString = startDate.toISOString().slice(0, 10);
        setActualStartDate(startDateString);
      } else {
        setActualStartDate('');
      }
      
      if (jobData.actualWorkCompleteDateTime) {
        const endDate = new Date(jobData.actualWorkCompleteDateTime);
        const endDateString = endDate.toISOString().slice(0, 10);
        setActualCompletionDate(endDateString);
      } else {
        setActualCompletionDate('');
      }
      
      setActualStartTime('');
      setActualCompletionTime('');
    }

    // Bind amounts
    setHourlyRate(jobData.publishedHourlyRate || '');
    setTotalAmount(jobData.publishedPeriodTotalAmount || '');

    // Bind remarks from API (always bind, not just for submitted/rejected)
    setRemarks(jobData.remarksByProfessional || '');
    
    // Bind work status from API if available
    if (jobData.jobStatus) {
      setWorkStatus(jobData.jobStatus);
    } else {
      setWorkStatus('');
    }
    
    setShowWorkStatusModal(true);
  };


  // Handle Close Work Status Modal
  const handleCloseWorkStatusModal = () => {
    setShowWorkStatusModal(false);
    setSelectedRequestId(null);
    setSelectedRequest(null);
    setPeriodType('for a shift');
    setWorkStatus('');
    setActualStartTime('');
    setActualCompletionTime('');
    setActualStartDate('');
    setActualCompletionDate('');
    setHourlyRate('');
    setTotalAmount('');
    setRemarks('');
  };

  // Handle Submit Work Status Update
  const handleSubmitWorkStatus = async () => {

    if (!workStatus) {
      toast.error('Please select work status');
      return;
    } // Period-specific validation
    if (periodType === 'for a shift') {
      if (!actualStartTime || !actualCompletionTime) {
        toast.error('Actual Start Time and Actual Completion Date & Time are required');
        return;
      }
      
      // Validate completion time is after start time
      const startDateTime = new Date(actualStartTime);
      const completionDateTime = new Date(actualCompletionTime);
      if (completionDateTime <= startDateTime) {
        toast.error('Actual Completion Date and Time must be after Actual Start Time');
        return;
      }
    }

    if (periodType === 'for a period') {
      if (!actualStartDate || !actualCompletionDate) {
        toast.error('Actual Start Date and Actual Completion Date are required');
        return;
      }
      
      // Validate completion date is after or equal to start date
      const startDate = new Date(actualStartDate);
      const completionDate = new Date(actualCompletionDate);
      if (completionDate < startDate) {
        toast.error('Actual Completion Date must be on or after Actual Start Date');
        return;
      }
    }
    setIsUpdatingStatus(true);
    
    // Convert date/time strings to ISO DateTime format
    let actualStartDateTime = null;
    let actualCompletionDateTime = null;
    
    if (periodType === 'for a shift') {
      // For shift: datetime-local inputs already include date and time (format: "2025-12-17T14:30")
      // Just append :00 for seconds
      if (actualStartTime) {
        actualStartDateTime = `${actualStartTime}:00`;
      }
      if (actualCompletionTime) {
        actualCompletionDateTime = `${actualCompletionTime}:00`;
      }
    } else if (periodType === 'for a period') {
      // For period: add default time (00:00:00) to dates
      if (actualStartDate) {
        actualStartDateTime = `${actualStartDate}T00:00:00`;
      }
      if (actualCompletionDate) {
        actualCompletionDateTime = `${actualCompletionDate}T00:00:00`;
      }
    }
    
    const payload = {
      jobID: selectedRequestId,
      workStatus,
      actualStartDate: actualStartDateTime,
      actualCompletionDate: actualCompletionDateTime,
      remarks
    };

    try {
      const response = await client.post(
        '/api/healthcare-professionals/jobs/complete',
        payload
      );

      if (response.data?.success) {
        toast.success('Work status updated successfully');
        handleCloseWorkStatusModal();
        fetchWorkHistory();
      } else {
        toast.error(response.data?.message || 'Failed to update status');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };


  // Format date and time for duty display
  const formatDutyDateTime = (request) => {
    if (!request) return '';
    // Handle period string from API (e.g., "17.Dec.2025 12:00 PM - Duration 9.00 Hrs")
    if (request.period) {
      return request.period;
    }
    // Fallback to old format
    const date = formatDate(request.periodFrom);
    let time = '3PM';
    if (request.startTime) {
      if (request.startTime.includes(':')) {
        const [hours, minutes] = request.startTime.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        time = `${hour12}${minutes !== '00' ? ':' + minutes : ''}${ampm}`;
      } else {
        time = request.startTime;
      }
    }
    const duration = request.duration ? `${request.duration}Hrs` : '8Hrs';
    return `${date} ${time}. Duration ${duration}`;
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    if (!status) return 'badge bg-secondary';
    const statusLower = status.toLowerCase();
    if (statusLower === 'assigned') return 'badge bg-primary';
    if (statusLower === 'completed' || statusLower === 'approved') return 'badge bg-success';
    if (statusLower === 'submitted') return 'badge bg-warning text-dark';
    if (statusLower === 'pending') return 'badge bg-warning text-dark';
    if (statusLower === 'unassigned') return 'badge bg-warning text-dark';    
    if (statusLower === 'applied') return 'badge bg-info';
    if (statusLower === 'withdrawn') return 'badge bg-warning';
    if (statusLower === 'rejected') return 'badge bg-danger';
    return 'badge bg-secondary';
  };

  // Get filtered applied requests based on status filter
  const getFilteredAppliedRequests = () => {
    if (statusFilter === 'All') return appliedRequests;
    return appliedRequests.filter(request => {
      const status = (request.jobStatus || request.status || '').toLowerCase();
      return status === statusFilter.toLowerCase();
    });
  };

  // Get filtered open opportunities based on status filter
  const getFilteredOpenOpportunities = () => {
    if (opportunityStatusFilter === 'All') return openOpportunities;
    return openOpportunities.filter(opportunity => {
      const canApply = opportunity.idProfessionalSelected === null || opportunity.idProfessionalSelected === undefined;
      if (opportunityStatusFilter === 'Open') {
        return canApply;
      } else if (opportunityStatusFilter === 'Position Filled') {
        return !canApply;
      }
      return true;
    });
  };

  return (
    <div className="App">
      {/* Common Dashboard Header */}
      <DashboardHeader
        userType={(user?.role || 'professional')?.toLowerCase()}
        userName={user?.displayName || 'Professional'}
        onMenuClick={(menuItem) => {
          if (menuItem === 'applied') {
            setActiveTab('applied');
          } else if (menuItem === 'opportunities') {
            setActiveTab('opportunities');
          } else if (menuItem === 'modify-profile') {
            handleModifyProfile();
          }
        }}
      />

      {/* Main Content */}
      <main className="dashboard-content">
        <div className="container">
          <div className="row">
            <div className="col-12">
              {/* Navigation Tabs */}
              <div className="dashboard-nav mb-4">
                <button
                  className={`dashboard-nav-btn ${activeTab === 'opportunities' ? 'active' : ''}`}
                  onClick={() => setActiveTab('opportunities')}
                >
                  <FaBriefcase className="me-2" />Open Opportunities
                </button>

                <button
                  className={`dashboard-nav-btn ${activeTab === 'applied' ? 'active' : ''}`}
                  onClick={() => setActiveTab('applied')}
                >
                  <FaFileAlt className="me-2" />Work History
                </button>

              </div>

              {/* Applied Requests Section */}
              {activeTab === 'applied' && (
                <div>
                  <div className="d-block d-md-flex justify-content-between align-items-center mb-4">
                    <h2 className="dashboard-title mb-2">Applied Requests</h2>
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('table')}
                        title="Table View"
                      >
                        <FaList />
                      </button>
                      <button
                        type="button"
                        className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('grid')}
                        title="Grid View"
                      >
                        <FaTh />
                      </button>
                    </div>
                  </div>

                  {/* Filter Section */}
                  <div className="mb-4">
                    <div className="row align-items-end">
                      <div className="col-md-3 mb-3 mb-md-0">
                        <label htmlFor="districtFilter" className="form-label">District</label>
                        <select
                          className="form-select"
                          id="districtFilter"
                          value={districtFilter || 'All'}
                          onChange={handleDistrictFilterChange}
                          disabled={isLoadingDistricts}
                        >
                          <option value="All">All Districts</option>
                          {districts.map(district => (
                            <option key={district.idDistrict} value={district.idDistrict}>
                              {district.districtName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3 mb-3 mb-md-0">
                        <label htmlFor="statusFilter" className="form-label">Status</label>
                        <select
                          className="form-select"
                          id="statusFilter"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="All">All</option>
                          <option value="Assigned">Assigned</option>
                          <option value="Submitted">Submitted</option>
                          <option value="Completed">Completed</option>
                          <option value="Approved">Approved</option>
                          <option value="Withdrawn">Withdrawn</option>
                          <option value="Unassigned">Unassigned</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Applied Requests Table View */}
                  {viewMode === 'table' && (
                    <div className="card">
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead className="table-light">
                              <tr>
                                <th scope="col">Hospital</th>
                                <th scope="col">Position</th>
                                <th scope="col">Location</th>
                                <th scope="col">Type</th>
                                <th scope="col">Total Amount</th>
                                <th scope="col">Period</th>
                                <th scope="col">Status</th>
                                <th scope="col">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {isLoadingWorkHistory ? (
                                <tr>
                                  <td colSpan="8" className="text-center">Loading...</td>
                                </tr>
                              ) : getFilteredAppliedRequests().length === 0 ? (
                                <tr>
                                  <td colSpan="8" className="text-center">No applied requests found</td>
                                </tr>
                              ) : (
                                getFilteredAppliedRequests().map((request) => (
                                  <tr key={request.jobID || request.id}>
                                    <td>{request.hospitalName || request.hospital}</td>
                                    <td>{request.position}</td>
                                    <td>{request.location}</td>
                                    <td>{request.jobDurationType || '-'}</td>                                
                                    <td  className="text-end">{request.totalAmount ? `Rs. ${formatINRNumber(request.totalAmount)}` : '-'}</td>
                                    <td>{request.period || '-'}</td>
                                    <td>
                                      <span className={getStatusBadgeClass(request.jobStatus)}>
                                        {request.jobStatus || '-'}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="d-flex gap-2">
                                        <button
                                          className="btn btn-outline-primary btn-sm"
                                          disabled={
                                            !(
                                              request.jobStatus?.toLowerCase() === 'assigned' ||
                                              request.jobStatus?.toLowerCase() === 'rejected' ||
                                              request.jobStatus?.toLowerCase() === 'submitted' ||
                                              request.jobStatus?.toLowerCase() === 'approved'
                                            )
                                          }
                                          onClick={() =>
                                            handleWorkStatusUpdate(request.jobID || request.id)
                                          }
                                        >
                                          {request.jobStatus?.toLowerCase() === 'approved' ? 'View Time Sheet' : 'Time Sheet Update'}
                                        </button>
                                        {request.canWithdraw && (
                                          <button
                                            className="btn btn-outline-danger btn-sm"
                                            title="Withdraw Job"
                                            onClick={() => handleWithdraw(request.jobID || request.id, request.applicationID || request.applicationId || request.jobApplicationId || request.idApplication)}
                                            disabled={isWithdrawing}
                                          >
                                            <FaTimes />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}


                  {/* Applied Requests Grid View */}
                  {viewMode === 'grid' && (
                    <div className="row">
                      {isLoadingWorkHistory ? (
                        <div className="col-12">
                          <div className="card">
                            <div className="card-body text-center py-5">
                              <p className="text-muted">Loading...</p>
                            </div>
                          </div>
                        </div>
                      ) : getFilteredAppliedRequests().length === 0 ? (
                        <div className="col-12">
                          <div className="card">
                            <div className="card-body text-center py-5">
                              <p className="text-muted">No applied requests found</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="col-12 mb-3">
                          <div className="row g-3">
                            {getFilteredAppliedRequests().map((request) => (
                              <div key={request.jobID || request.id} className="col-12 col-md-6 col-lg-4">
                                <div className="card h-100 opportunity-card modern-card">
                                  <div className="card-body d-flex flex-column p-0">
                                    {/* Header Section with Position */}
                                    <div className="card-header-section">
                                      <h5 className="card-position-title">{request.position}</h5>
                                      <span className={getStatusBadgeClass(request.jobStatus || request.status)}>
                                        {request.jobStatus || request.status || '-'}
                                      </span>
                                    </div>

                                    {/* Hospital Info Section */}
                                    <div className="card-info-section">
                                      <div className="info-item">
                                        <div className="info-icon hospital-icon">
                                          <FaHospital />
                                        </div>
                                        <div className="info-content">
                                          <div className="info-label">Hospital</div>
                                          <div className="info-value">{request.hospitalName || request.hospital}</div>
                                        </div>
                                      </div>

                                      <div className="info-item">
                                        <div className="info-icon location-icon">
                                          <FaMapMarkerAlt />
                                        </div>
                                        <div className="info-content">
                                          <div className="info-label">Location</div>
                                          <div className="info-value">{request.location}</div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="card-divider"></div>

                                    {/* Details Section */}
                                    <div className="card-details-section">

                                      <div className="detail-item">

                                        <FaFileAlt className="detail-icon" />
                                        <div className="detail-content">
                                          <span className="detail-label">Type</span>
                                          <span className="detail-value">
                                            {request.jobDurationType || '-'}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="detail-item">
                                        <FaCalendarAlt className="detail-icon" />
                                        <div className="detail-content">
                                          <span className="detail-label">Period</span>
                                          <span className="detail-value">{request.period || '-'}</span>
                                        </div>
                                      </div>
                                      <div className="detail-item">
                                        <FaRupeeSign className="detail-icon" />
                                        <div className="detail-content">
                                          <span className="detail-label">Total Amount</span>
                                          <span className="detail-value">
                                            {request.totalAmount ? `Rs. ${request.totalAmount}` : '-'}
                                          </span>
                                        </div>
                                      </div>

                                      <div className="py-2">
                                        <button
                                          className="btn btn-outline-primary  w-100 py-2"
                                          disabled={!(request.jobStatus.toLowerCase() === "assigned" || request.jobStatus.toLowerCase() === "rejected" || request.jobStatus.toLowerCase() === "submitted" || request.jobStatus.toLowerCase() === "approved")}
                                          onClick={() => {
                                            handleWorkStatusUpdate(request.jobID || request.id);
                                          }}
                                        >
                                          {request.jobStatus?.toLowerCase() === 'approved' ? 'View Time Sheet' : 'Time Sheet Update'}
                                        </button>
                                        {request.canWithdraw && (
                                          <button
                                            className="btn btn-outline-danger w-100 py-2 mt-2"
                                            title="Withdraw Job"
                                            onClick={() => handleWithdraw(request.jobID || request.id, request.applicationID || request.applicationId || request.jobApplicationId || request.idApplication)}
                                            disabled={isWithdrawing}
                                          >
                                            <FaTimes className="me-2" />
                                            Withdraw
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Open Opportunities Section */}
              {activeTab === 'opportunities' && (
                <div>
                  <div className="d-block d-md-flex justify-content-between align-items-center mb-4">
                    <h2 className="dashboard-title mb-2">Open Opportunities</h2>
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('table')}
                        title="Table View"
                      >
                        <FaList />
                      </button>
                      <button
                        type="button"
                        className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('grid')}
                        title="Grid View"
                      >
                        <FaTh />
                      </button>
                    </div>
                  </div>

                  {/* Filter Section */}
                  <div className="mb-4">
                    <div className="row align-items-end">
                      <div className="col-md-3 mb-3 mb-md-0">
                        <label htmlFor="districtFilterOpportunities" className="form-label">District</label>
                        <select
                          className="form-select"
                          id="districtFilterOpportunities"
                          value={districtFilter || 'All'}
                          onChange={handleDistrictFilterChange}
                          disabled={isLoadingDistricts}
                        >
                          <option value="All">All Districts</option>
                          {districts.map(district => (
                            <option key={district.idDistrict} value={district.idDistrict}>
                              {district.districtName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3 mb-3 mb-md-0">
                        <label htmlFor="opportunityStatusFilter" className="form-label">Status</label>
                        <select
                          className="form-select"
                          id="opportunityStatusFilter"
                          value={opportunityStatusFilter}
                          onChange={(e) => setOpportunityStatusFilter(e.target.value)}
                        >
                          <option value="All">All</option>
                          <option value="Open">Open</option>
                          <option value="Position Filled">Position Filled</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Open Opportunities Table View */}
                  {viewMode === 'table' && (
                    <div className="card">
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead className="table-light">
                              <tr>
                                <th scope="col">Hospital</th>
                                <th scope="col">Position</th>
                                <th scope="col">Location</th>
                                <th scope="col">Type</th>
                                <th scope="col">Total Amount</th>
                                <th scope="col">Period</th>
                                <th scope="col">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {isLoadingJobListings ? (
                                <tr>
                                  <td colSpan="6" className="text-center">Loading...</td>
                                </tr>
                              ) : getFilteredOpenOpportunities().length === 0 ? (
                                <tr>
                                  <td colSpan="6" className="text-center">No open opportunities found</td>
                                </tr>
                              ) : (
                                getFilteredOpenOpportunities().map((opportunity) => {
                                  const canApply = opportunity.idProfessionalSelected === null || opportunity.idProfessionalSelected === undefined;
                                  const isCurrentlyApplying = isApplying && applyingJobId === (opportunity.jobID || opportunity.id);
                                  return (
                                    <tr key={opportunity.jobID || opportunity.id}>
                                      <td>{opportunity.hospitalName || opportunity.hospital}</td>
                                      <td>{opportunity.position}</td>
                                      <td>{opportunity.location}</td>
                                      <td>{opportunity.jobDurationType || '-'}</td>
                                      <td  className="text-end">{opportunity.totalAmount ? `Rs. ${formatINRNumber(opportunity.totalAmount)}` : '-'}</td>
                                   
                                      <td>{opportunity.period || '-'}</td>
                                      <td>
                                        <button
                                          className="btn btn-primary btn-sm"
                                          onClick={() => handleApply(opportunity.jobID || opportunity.id)}
                                          disabled={!canApply || isCurrentlyApplying}
                                          title={!canApply ? 'This position has already been filled' : ''}
                                        >
                                          <FaBriefcase className="me-1" />                                        
                                          {isCurrentlyApplying ? 'Applying...' : canApply ? 'Apply Now' : 'Position Filled'}
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Open Opportunities Grid View */}
                  {viewMode === 'grid' && (
                    <div className="row">
                      {isLoadingJobListings ? (
                        <div className="col-12">
                          <div className="card">
                            <div className="card-body text-center py-5">
                              <p className="text-muted">Loading...</p>
                            </div>
                          </div>
                        </div>
                      ) : getFilteredOpenOpportunities().length === 0 ? (
                        <div className="col-12">
                          <div className="card">
                            <div className="card-body text-center py-5">
                              <p className="text-muted">No open opportunities found</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="col-12 mb-3">
                          <div className="row g-3">
                            {getFilteredOpenOpportunities().map((opportunity) => (
                              <div key={opportunity.jobID || opportunity.id} className="col-12 col-md-6 col-lg-4">
                                <div className="card h-100 opportunity-card modern-card">
                                  <div className="card-body d-flex flex-column p-0">
                                    {/* Header Section with Position */}
                                    <div className="card-header-section">
                                      <h5 className="card-position-title">{opportunity.position}</h5>
                                    </div>

                                    {/* Hospital Info Section */}
                                    <div className="card-info-section">
                                      <div className="info-item">
                                        <div className="info-icon hospital-icon">
                                          <FaHospital />
                                        </div>
                                        <div className="info-content">
                                          <div className="info-label">Hospital</div>
                                          <div className="info-value">{opportunity.hospitalName || opportunity.hospital}</div>
                                        </div>
                                      </div>

                                      <div className="info-item">
                                        <div className="info-icon location-icon">
                                          <FaMapMarkerAlt />
                                        </div>
                                        <div className="info-content">
                                          <div className="info-label">Location</div>
                                          <div className="info-value">{opportunity.location}</div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="card-divider"></div>

                                    {/* Details Section */}
                                    <div className="card-details-section">
                                      <div className="detail-item">

                                        <FaFileAlt className="detail-icon" />
                                        <div className="detail-content">
                                          <span className="detail-label">Type</span>
                                          <span className="detail-value">
                                            {opportunity.jobDurationType || '-'}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="detail-item">
                                        <FaCalendarAlt className="detail-icon" />
                                        <div className="detail-content">
                                          <span className="detail-label">Job Details</span>
                                          <span className="detail-value">{opportunity.period || '-'}</span>
                                        </div>
                                      </div>
                                      <div className="detail-item">
                                        <FaRupeeSign className="detail-icon" />
                                        <div className="detail-content">
                                          <span className="detail-label">Total Amount</span>
                                          <span className="detail-value">
                                            {opportunity.totalAmount ? `Rs. ${opportunity.totalAmount}` : '-'}
                                          </span>
                                        </div>
                                      </div>

                                      {/* Apply Button */}
                                      <div className="mt-3">
                                        {(() => {
                                          const canApply = opportunity.idProfessionalSelected === null || opportunity.idProfessionalSelected === undefined;
                                          const isCurrentlyApplying = isApplying && applyingJobId === (opportunity.jobID || opportunity.id);
                                          return (
                                            <button
                                              className="btn btn-primary w-100 apply-btn"
                                              onClick={() => handleApply(opportunity.jobID || opportunity.id)}
                                              disabled={!canApply || isCurrentlyApplying}
                                              title={!canApply ? 'This position has already been filled' : ''}
                                            >
                                              <FaBriefcase className="me-2" />
                                              {isCurrentlyApplying ? 'Applying...' : canApply ? 'Apply Now' : 'Position Filled'}
                                            </button>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Work Status Update Modal */}
      <Modal
        show={showWorkStatusModal}
        onHide={handleCloseWorkStatusModal}
        centered
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Work Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div className="row m-0">
              <div className="col-6 p-2">
                <label className="form-label mb-1">Date & Time of Duty</label>
                <input
                  className="form-control"
                  type="text"
                  value={selectedRequest?.period || ''}
                  readOnly
                />
              </div>
              <div className="col-6 p-2">
                <label className="form-label mb-1">Period</label>
                <div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="periodType"
                      id="periodShift"
                      value="for a shift"
                      checked={periodType === "for a shift"}
                      onChange={(e) => setPeriodType(e.target.value)}
                      disabled
                    />
                    <label className="form-check-label" htmlFor="periodShift">
                      for a shift
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="periodType"
                      id="periodPeriod"
                      value="for a period"
                      checked={periodType === "for a period"}
                      onChange={(e) => setPeriodType(e.target.value)}
                      disabled
                    />
                    <label className="form-check-label" htmlFor="periodPeriod">
                      for a period
                    </label>
                  </div>
                </div>

              </div>

              {periodType === "for a shift" && (
                <>
                  <div className="col-6 p-2">
                    <label className="form-label mb-1">
                      Actual Start Date and Time <span className="text-danger">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={actualStartTime}
                      onChange={(e) => setActualStartTime(e.target.value)}
                      readOnly={!(['submitted', 'rejected', 'assigned'].includes(selectedRequest?.jobStatus?.toLowerCase()))}
                      required
                    />
                  </div>

                  <div className="col-6 p-2">
                    <label className="form-label mb-1">
                      Actual Completion Date and Time <span className="text-danger">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={actualCompletionTime}
                      onChange={(e) => setActualCompletionTime(e.target.value)}
                      min={
                        actualStartTime
                          ? (() => {
                              const start = new Date(actualStartTime);
                              start.setMinutes(start.getMinutes() + 1);
                              return start.toISOString().slice(0, 16);
                            })()
                          : ''
                      }
                      readOnly={!(['submitted', 'rejected', 'assigned'].includes(selectedRequest?.jobStatus?.toLowerCase()))}
                      required
                    />
                  </div>

                  <div className="col-6 p-2">
                    <label className="form-label mb-1">Hourly Amount</label>
                    <input
                      type="number"
                      className="form-control"
                      value={hourlyRate}
                      readOnly
                    />
                  </div>
                </>
              )}
              {periodType === "for a period" && (
                <>
                  <div className="col-6 p-2">
                    <label className="form-label mb-1">
                      Actual Start Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={actualStartDate}
                      onChange={(e) => setActualStartDate(e.target.value)}
                      readOnly={!(['submitted', 'rejected', 'assigned'].includes(selectedRequest?.jobStatus?.toLowerCase()))}
                      required
                    />
                  </div>

                  <div className="col-6 p-2">
                    <label className="form-label mb-1">
                      Actual Completion Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={actualCompletionDate}
                      onChange={(e) => setActualCompletionDate(e.target.value)}
                      min={actualStartDate || ''}
                      readOnly={!(['submitted', 'rejected', 'assigned'].includes(selectedRequest?.jobStatus?.toLowerCase()))}
                      required
                    />
                  </div>
                </>
              )}


              <div className="col-6 p-2">
                <label className="form-label mb-1">Total Amount </label>
                <input
                  type="number"
                  className="form-control"
                  value={totalAmount}
                  readOnly
                />
              </div>
              <div className="col-12 p-2">
                <label className="form-label mb-1">Remarks {['submitted', 'rejected', 'assigned'].includes(selectedRequest?.jobStatus?.toLowerCase()) && <span className="text-danger">*</span>}</label>
                <textarea
                  className="form-control"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  readOnly={!(['submitted', 'rejected', 'assigned'].includes(selectedRequest?.jobStatus?.toLowerCase()))}
                />
              </div>
              {selectedRequest?.jobStatus?.toLowerCase() !== 'approved' && (
                <div className="col-12 p-2">
                  <label htmlFor="workStatus" className="form-label">
                    Work Status <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    id="workStatus"
                    value={workStatus}
                    onChange={(e) => setWorkStatus(e.target.value)}
                    disabled={!(['submitted', 'rejected', 'assigned'].includes(selectedRequest?.jobStatus?.toLowerCase()))}
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
              )}
            </div>

          )}

        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCloseWorkStatusModal}
            disabled={isUpdatingStatus}
          >
            Cancel
          </button>
          {(['submitted', 'rejected', 'assigned'].includes(selectedRequest?.jobStatus?.toLowerCase())) && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmitWorkStatus}
              disabled={isUpdatingStatus}
            >
              {isUpdatingStatus ? 'Updating...' : 'Update Status'}
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ProfessionalDashboard;

